import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "context/TranslationProvider";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import Pagination from "../components/Pagination";
import { Button, UncontrolledTooltip } from "reactstrap";
import helpers from "utils/helpers";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DropdownFilter from "utils/DropdownFilter";
import { useAuth } from "context/AuthContext";
import CloseIcon from "@mui/icons-material/Close";

const STATUS_FILTER_OPTIONS = (t) => [
  { value: "all", label: t("common.all", "All") },
  { value: "active", label: t("promoCodes.status.active", "Active") },
  { value: "inactive", label: t("promoCodes.status.inactive", "Inactive") },
];

function PromoCodesTable({ items, onEdit, deleteRow }) {
  const { t, language } = useTranslation();
  const componentRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const handleClearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setCurrentPage(0);
  };
  const { permission } = useAuth();
  const promoPermission = permission?.permissions?.modules?.promoCodes;
  const canCreate = promoPermission?.create;
  const canUpdate = promoPermission?.update;
  const canDelete = promoPermission?.delete;

  const filteredItems = useMemo(() => {
    let base = items;

    if (statusFilter === "active") base = base.filter((i) => i.isActive);
    else if (statusFilter === "inactive") base = base.filter((i) => !i.isActive);

    const search = searchText.toLowerCase();
    return base.filter((item) =>
      (item.code || "").toLowerCase().includes(search) ||
      (item.description || "").toLowerCase().includes(search)
    );
  }, [items, statusFilter, searchText]);

  const paginatedItems = useMemo(
    () => filteredItems.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage),
    [filteredItems, currentPage, rowsPerPage]
  );

  const columns = [
    {
      dataField: "title",
      text: t("promoCodes.fields.title", "Title"),
      sort: true,
      headerAlign: "left",
      align: "left",
    },
    {
      dataField: "code",
      text: t("promoCodes.fields.code", "Code"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (cell) => (
        <span className="font-weight-bold text-dark" style={{ letterSpacing: "0.03em" }}>
          {cell}
        </span>
      ),
    },
    {
      dataField: "discountValue",
      text: t("promoCodes.fields.discount", "Discount"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (cell, row) =>
        cell != null ? (
            <span className="text-dark">
            {row.discountType === "percentage" ? `${cell}%` : `€${cell}`}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      dataField: "isActive",
      text: t("table.headers.status", "Status"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (cell) =>
        cell ? (
          <span className="badge badge-success">
            {t("promoCodes.status.active", "Active")}
          </span>
        ) : (
          <span className="badge badge-secondary">
            {t("promoCodes.status.inactive", "Inactive")}
          </span>
        ),
    },
    {
      dataField: "validUntil",
      text: t("promoCodes.fields.validUntil", "Valid Until"),
      headerAlign: "left",
      align: "left",
      formatter: (cell) =>
        cell ? helpers.getFormattedDate(cell, t) : <span className="text-muted">{t("promoCodes.noExpiry", "No expiry")}</span>,
    },
    {
      dataField: "usedCount",
      text: t("promoCodes.usage", "Usage"),
      headerAlign: "left",
      align: "left",
      formatter: (cell, row) => {
        const used = cell ?? 0;
        const limit = row.maxUses;
        return limit != null ? `${used} / ${limit}` : `${used} / ∞`;
      },
    },
    {
      dataField: "createdAt",
      text: t("table.headers.createdAt", "Created At"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (cell) => helpers.getFormattedDate(cell, t),
    },
    {
      dataField: "actions",
      text: t("table.headers.actions", "Actions"),
      headerAlign: "left",
      align: "left",
      formatter: (_, row) => (
        <div className="d-flex align-items-center gap-1">
          {canUpdate && (
            <>
              <Button size="sm" id={`edit-promo-${row._id}`} onClick={() => onEdit(row)}>
                <EditIcon fontSize="small" color="info" />
              </Button>
              <UncontrolledTooltip target={`edit-promo-${row._id}`} key={`edit-promo-${row._id}-${language}`}>
                {t("page.roles.actions.edit", "Edit")}
              </UncontrolledTooltip>
            </>
          )}
          {canDelete && (
            <>
              <Button size="sm" id={`delete-promo-${row._id}`} onClick={() => deleteRow(row)}>
                <DeleteIcon fontSize="small" color="error" />
              </Button>
              <UncontrolledTooltip target={`delete-promo-${row._id}`} key={`delete-promo-${row._id}-${language}`}>
                {t("page.flagged.buttons.delete", "Delete")}
              </UncontrolledTooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <ToolkitProvider
      data={paginatedItems}
      key={language}
      keyField="_id"
      columns={columns}
      search
    >
      {(props) => (
        <div className="py-4 w-100 d-flex flex-column" ref={componentRef}>
          <div
            id="datatable-promo_filter"
            className="dataTables_filter px-4 pb-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
          >
            <div className="d-flex align-items-center mb-2 mb-md-0">
              <span className="text-sm font-medium text-slate-600 mr-2">
                {t("table.search", "Search")}:
              </span>
              <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                <input
                  className="form-control form-control-sm"
                  placeholder={t("table.search", "Search")}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(0);
                  }}
                  style={{ paddingRight: "24px" }}
                />
                {searchText && (
                  <span
                    onClick={() => {
                      setSearchText("");
                      setCurrentPage(0);
                    }}
                    className="position-absolute cursor-pointer search-clear-btn"
                  >
                    <CloseIcon style={{ fontSize: "14px" }} />
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center">
              {canCreate && (
                <Button
                  color="warning"
                  size="sm"
                  className="mb-2 mb-sm-0 mr-sm-2 w-100 w-sm-auto"
                  onClick={() => onEdit(null)}
                >
                  <AddIcon fontSize="small" className="mr-1" />
                  {t("promoCodes.create", "Create Promo Code")}
                </Button>
              )}

              <div className="w-100 w-sm-auto d-flex align-items-center">
                <DropdownFilter
                  label={t("table.filterBy", "Filter By")}
                  value={statusFilter}
                  options={STATUS_FILTER_OPTIONS(t)}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setCurrentPage(0);
                  }}
                />
                <Button
                  size="sm"
                  className="ml-2 btn-neutral text-danger font-weight-600"
                  onClick={handleClearFilters}
                >
                  {t("common.clearFilters", "Clear Filters")}
                </Button>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <BootstrapTable
              {...props.baseProps}
              bootstrap4
              bordered={false}
              wrapperClasses="max-h-[60vh] overflow-y-auto"
              noDataIndication={() => (
                <div className="text-center text-muted py-4">
                  {t("promoCodes.noData", "No promo codes found")}
                </div>
              )}
            />
          </div>

          <Pagination
            totalRows={filteredItems.length}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onRowsPerPageChange={(size) => setRowsPerPage(size)}
            t={t}
          />
        </div>
      )}
    </ToolkitProvider>
  );
}

export default PromoCodesTable;
